import React, { useEffect, useRef, useContext, useState } from 'react'

import { objectCheck } from '../../../sections/options/sections/GlobalCashRegisters/pages/DeviceUpdate/DeviceUpdate'
import { useTranslation } from 'react-i18next'
import { Col } from 'react-flexbox-grid'
import { DataContextByDiffPages } from 'components/SectionHandler/components/SectionHandler'
import { ServiceModal } from 'components/Modal'
import Button from 'components/Button'
import Divider from 'components/Divider'
import { Checkbox, Text, DateTimePicker, Number, Form, Select, WeekdayPicker } from 'components/forms'
import { isEqual } from 'lodash'

const useMultiUpdate = (props) => {
	const { multiEdit, formData, newRecord, multiEditConfig = [] } = props
	const apiRef = useRef()
	const [t] = useTranslation()
	const [openModal, setOpenModal] = useState(false)
	const { changedValues, setChangedValues } = useContext(DataContextByDiffPages)

	useEffect(() => {
		if(!multiEdit?.isActive) {
			if(typeof (setChangedValues) === 'function') {
				setChangedValues({})
			}
			if(apiRef.current) {
				apiRef.current.setValues(formData)
			}
		}
	}, [multiEdit?.isActive])

	const toggleModal = () => setOpenModal(prev => !prev)

	const changedValuesToArray = Object.keys(changedValues || {})?.length
	const sendChangesFromModal = async () => {
		setOpenModal(false)
		await multiEdit.handleMultiEdit({ fields: changedValues })
		setChangedValues({})
	}

	const onChangeMultiEdit = (e, values) => {
		Object.keys(e?.values).forEach((key) => {
			const eventDataProp = e?.values[key]
			const formDataProp = values[key]
			const keyChangedValues = changedValues[key]
			const condition =
			(!isEqual(eventDataProp, formDataProp) && !Array.isArray(eventDataProp))
			||
			((!isEqual(eventDataProp, keyChangedValues) && changedValues.hasOwnProperty(key)
			&& !Array.isArray(eventDataProp)))
			if(condition) {
				setChangedValues((previousVal) => {
					return { ...previousVal, [key]: eventDataProp }
				})
			}
		})
	}

	const ConfirmButton = (formState) => {
		return (
			multiEdit.isActive && !newRecord && <div>
				<Divider height={20}/>
				<Col>
					<Button
						onClick={toggleModal}
						disabled={!changedValuesToArray}
						status={changedValuesToArray && !objectCheck(formState?.errors) ? 'primary' : 'inactive'}
						text={t('common.buttons.confirm')} />
				</Col>
			</div>
		)
	}

	const ModalFields = () => {
		// Specific types for your components which you need to visible in modal window:
		// date - you will be have date *from and *to
		// dateTime - you will be have a timestamp
		// oneDate - you will be have only *first date
		// selectWithoutAdditionalField - you will be have select without *labelField & *valueField
		// simpleSelect - you will be have select with *simpleValue = false
		return (
			// eslint-disable-next-line array-callback-return
			multiEditConfig.map((elem) => {
				const keys = Object.keys(changedValues)
				return keys.map((e, idx) => {
					switch (true) {
						case elem.type === 'checkbox' && e === elem.fieldName:
							return (<Checkbox
								key={idx}
								inline={true}
								label={elem.label}
								field={elem.fieldName}
								noForm
								disabled
								checked={changedValues?.[e]}
							/>)
						case elem.type === 'text' && e === elem.fieldName:
							return (<Text
								key={idx}
								label={elem.label}
								field={elem.fieldName}
								noForm
								value={changedValues?.[e]}
								inline
								disabled
							/>)
						case elem.type === 'date' && e === elem.fieldName:
							return (
								<DateTimePicker
									key={idx}
									field={elem.fieldName}
									label={elem.label}
									value={changedValues?.[e]}
									style={{ marginRight: '20px' }}
									interval
									picker='date'
									placeholder={{ from: t('common.placeholders.from_date'), to: t('common.placeholders.to_date') }}
									noForm
									disabled
								/>
							)
						case elem.type === 'dateTime' && e === elem.fieldName:
							return (
								<DateTimePicker
									key={idx}
									field={elem.fieldName}
									label={elem.label}
									value={changedValues?.[e]}
									style={{ marginRight: '20px' }}
									interval
									picker='time'
									placeholder={{ from: t('common.placeholders.from_time'), to: t('common.placeholders.to_time') }}
									noForm
									disabled
								/>
							)
						case elem.type === 'oneDate' && e === elem.fieldName:
							return (
								<div>
									<DateTimePicker
										disabled
										picker='date'
										field={elem.fieldName}
										label={elem.label}
									/>
								</div>
							)
						case elem.type === 'number' && e === elem.fieldName:
							return (
								<Number
									key={idx}
									label={elem.label}
									field={elem.fieldName}
									value={changedValues?.[e]}
									noForm
									disabled
									placeholder={t('user_section.placeholders.performance')}
								/>
							)
						case elem.type === 'select' && e === elem.fieldName:
 							return (
								<div style={{ marginBottom: '18px' }}>
									<Select
										key={idx}
										inline
										style={{ width: '260px' }}
										label={elem.label}
										simpleValue
										valueField='id'
										labelField='name'
										disabled
										defaultValue={changedValues?.[e]}
										remoteOptions={{ ...elem.queryOptions, startFromId: changedValues?.[e] }}
 									/>
								</div>
 							)
						case elem.type === 'simpleSelect' && e === elem.fieldName:
							return (
								<Select
									key={idx}
									inline
									style={{ width: '260px' }}
									label={elem.label}
									valueField='id'
									simpleValue={false}
									labelField='name'
									disabled
									defaultValue={changedValues?.[e]}
									remoteOptions={elem.queryOptions}
								/>
							)
						case elem.type === 'selectWithoutAdditionalField' && e === elem.fieldName:
  							return (
  								<Select
  									key={idx}
  									inline
  									style={{ width: '260px' }}
  									label={elem.label}
  									simpleValue
  									disabled
  									defaultValue={changedValues?.[e]}
  									remoteOptions={elem.queryOptions}
  								/>
	 	 					)
						case elem.type === 'selectNumberOfPercent' && (changedValues?.[e]?.hasOwnProperty('numberOfPercent') || {}):
							return (
								<div style={{ marginBottom: '18px' }}>
									<Select
										key={idx}
										inline
										style={{ width: '260px' }}
										label={elem.label}
										simpleValue
										disabled
										defaultValue={changedValues?.[e].numberOfPercent}
										remoteOptions={elem.queryOptions}
									/>
								</div>
							)
						case elem.type === 'numberInputPercent' && (changedValues?.[e]?.hasOwnProperty('freePercent' || 'masterPercent') || {}):
							return (
								<Number
									key={idx}
									field={elem.fieldName}
									labelT={elem.label}
									precision={2}
									withControls={false}
									disabled
									shrink
								/>
							)
 						case elem.type === 'weekdayPicker' && e === elem.fieldName:
	 						return (
 								<WeekdayPicker
 									key={idx}
 									inline
 									field={elem.fieldName}
 									label={elem.label}
 									disabled
 									placeholder={t('common.placeholders.week_days')}
 								/>
 							)
						case elem.type === 'numberInput' && e === elem.fieldName:
							return (
								<Number
									key={idx}
									field={elem.fieldName}
									labelT={elem.label}
									precision={2}
									disabled
									withControls={false}
									shrink
								/>
							)
						default: return <div key={idx} />
					}
				})
			})
		)
	}

	const ConfirmModal = () => {
		return (
			openModal &&
				<ServiceModal
					onClose={toggleModal}
					label={t('common.buttons.confirm')}
					btnText={t('common.buttons.confirm')}
					btnCancelText={t('common.buttons.cancel')}
					onOk={sendChangesFromModal}
					text={t('user_section.strings.confirm_text')}
					width={655}>
					<div style={{ display: 'flex', margin: '15px', flexDirection: 'column', textAlign: 'initial' }}>
						<Form initialValues={changedValues}>
							<ModalFields />
						</Form>
	 				</div>
				</ServiceModal>)
	}

	return {
		ConfirmButton,
		ConfirmModal,
		onChangeMultiEdit,
		apiRef
	}
}

export default useMultiUpdate
